import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, Mail, Users, User, X, MessageSquare, ArrowLeft, AlertCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin console for:
 *   1. Sending ad-hoc emails to any subscriber, list, or all subs
 *   2. Manually closing an active alert (broadcasts exit now, faster than
 *      the 5-min watcher cadence).
 *
 * Both surfaces live here so the admin can pivot between "reach a user" and
 * "kill a position" without navigating.
 */
export default function AdminEmailConsole() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-mb-canvas" style={{ background: "var(--mb-canvas)" }}>
        <div className="max-w-6xl mx-auto px-6 py-12 space-y-10">
          <div>
            <Link to="/admin" className="text-sm text-mb-emerald inline-flex items-center gap-1 hover:underline">
              <ArrowLeft className="w-3 h-3" /> Back to review queue
            </Link>
            <h1 className="font-display font-semibold text-4xl mt-3">Desk tools</h1>
            <p className="text-sm text-mb-ink-muted mt-2">
              Push messages to subscribers and force-close active positions when the tape moves faster than the auto watcher.
            </p>
          </div>
          <EmailForm />
          <CloseActiveAlerts />
        </div>
      </div>
    </>
  );
}

function EmailForm() {
  const [audience, setAudience] = useState("all-subscribers");
  const [singleEmail, setSingleEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    const to = audience === "one" ? singleEmail.trim() : audience;
    if (audience === "one" && !to.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/email/send", {
        to,
        subject: subject.trim(),
        template: "note_from_desk",
        text_body: body.trim(),
      });
      toast.success(`Sent ${r.data.sent}/${r.data.attempted} · ${r.data.failed} failed`);
      if (r.data.failed === 0) {
        setSubject("");
        setBody("");
        if (audience === "one") setSingleEmail("");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <section className="rounded-xl border p-6" style={{ borderColor: "var(--mb-border)", background: "var(--mb-surface)" }}>
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-mb-emerald" />
        <h2 className="font-display font-semibold text-2xl">Send an email</h2>
      </div>
      <p className="text-sm text-mb-ink-muted mb-6">
        Uses the branded &quot;note from the desk&quot; template. Body paragraphs are separated by blank lines.
      </p>

      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        {[
          { key: "all-subscribers", label: "All subscribers", Icon: Users },
          { key: "all-users",       label: "All users",       Icon: Users },
          { key: "one",             label: "One user",        Icon: User },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setAudience(opt.key)}
            className={`rounded-md px-4 py-3 text-sm font-semibold text-left flex items-center gap-2 border ${
              audience === opt.key
                ? "bg-mb-emerald text-white border-mb-emerald"
                : "bg-white border-mb-border hover:border-mb-emerald/50"
            }`}
            data-testid={`audience-${opt.key}`}
          >
            <opt.Icon className="w-4 h-4" />
            {opt.label}
          </button>
        ))}
      </div>

      {audience === "one" && (
        <Input
          placeholder="subscriber@example.com"
          value={singleEmail}
          onChange={(e) => setSingleEmail(e.target.value)}
          className="mb-3"
          data-testid="admin-email-recipient"
        />
      )}

      <Input
        placeholder="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="mb-3"
        data-testid="admin-email-subject"
      />

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Message body… (paragraphs separated by blank lines)"
        rows={10}
        className="w-full rounded-md border border-mb-border bg-white p-3 text-sm font-sans"
        data-testid="admin-email-body"
      />

      <div className="mt-4 flex items-center gap-3">
        <Button
          onClick={send}
          disabled={sending}
          className="bg-mb-emerald hover:bg-mb-emerald-dark text-white"
          data-testid="admin-email-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send
        </Button>
        <span className="text-xs text-mb-ink-subtle">
          Recipient list resolves server-side. All emails carry the standard Moonbucks disclosure footer.
        </span>
      </div>
    </section>
  );
}

function CloseActiveAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/alerts/feed");
      const active = (r.data || []).filter((a) => !a.closed && a.status === "approved");
      setAlerts(active);
    } catch (e) {
      toast.error("Couldn't load active alerts");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line

  const closeAlert = async (a) => {
    if (!window.confirm(`Close ${a.symbol} now and blast exit to every subscriber?`)) return;
    setClosingId(a.id);
    try {
      const r = await api.post(`/admin/alerts/${a.id}/close?reason=analyst_manual`);
      toast.success(`${r.data.symbol} closed · ${r.data.final_return_pct != null ? r.data.final_return_pct.toFixed(2) + "%" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    }
    setClosingId(null);
  };

  return (
    <section className="rounded-xl border p-6" style={{ borderColor: "var(--mb-border)", background: "var(--mb-surface)" }}>
      <div className="flex items-center gap-2 mb-1">
        <AlertCircle className="w-4 h-4 text-red-500" />
        <h2 className="font-display font-semibold text-2xl">Close a live alert</h2>
      </div>
      <p className="text-sm text-mb-ink-muted mb-6">
        Manual override. Fires the same subscriber-facing Discord + email blast as an auto exit event, with reason
        &quot;analyst manual&quot;. Use when momentum breaks and the 5-min watcher is too slow.
      </p>

      {loading && (
        <div className="text-center py-8 text-sm text-mb-ink-muted">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading active alerts…
        </div>
      )}
      {!loading && alerts.length === 0 && (
        <div className="text-center py-8 text-sm text-mb-ink-muted">No active alerts right now.</div>
      )}
      {!loading && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between p-3 rounded-md border"
              style={{ borderColor: "var(--mb-border)", background: "var(--mb-surface-elevated)" }}
              data-testid={`active-alert-row-${a.symbol}`}
            >
              <div className="flex items-center gap-3">
                <span className="font-mono-data font-bold text-lg">{a.symbol}</span>
                <span className="text-xs uppercase tracking-widest text-mb-ink-subtle">
                  {a.alert_class || "steady"}
                </span>
                <span className="text-xs text-mb-ink-muted font-mono-data">
                  entry ${a.entry_price} · TP1 ${a.take_profit_price} · stop ${a.stop_loss_price}
                </span>
              </div>
              <Button
                variant="outline"
                onClick={() => closeAlert(a)}
                disabled={closingId === a.id}
                className="text-red-600 border-red-300 hover:bg-red-50 rounded-md"
                data-testid={`close-alert-${a.symbol}`}
              >
                {closingId === a.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><X className="w-4 h-4 mr-1" /> Close now</>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
